/* eslint-disable max-lines-per-function */
import nn from '@futuweb/tool-nn';
import NNQ from '@futuweb/tool-nn/dist/nnq';
import { ref, computed, onMounted } from 'vue-demi';
import { FREQUENCY_INTERVAL } from '../constants';
import { reportLog, errStr } from './report';
import { getPluginConfig } from './utils';
const { useLockScreen } = require('@fegobull/webapi').use;
const { deviceId: deviceIdHelper } = require('@futuweb/business-cipher');
const {
    store: { variable }
} = require('@futuweb/fego-render');
const {
    library: { dayjs }
} = require('@futuweb/fego-bridge');

export default function useModalStyle(props, emit) {
    const { initIntercept } = useIntercept(props);

    const visible = ref(false);

    const openModal = () => {
        visible.value = true;
    };

    const closeModal = () => {
        visible.value = false;
        emit('stay-modal_after-close');
    };

    if (props.lockScroll) {
        useLockScreen(visible);
    }

    onMounted(() => {
        // 初始化拦截
        initIntercept(openModal);
    });

    return {
        visible,
        openModal,
        closeModal
    };
}


function useIntercept(props) {
    // 插件配置
    const stayModalConfig  = getPluginConfig('stay-modal-config-plugin');
    // 应用 ID
    const appId = variable.data.APP_ID || window._appId;
    // 设备 ID
    const deviceId = deviceIdHelper.get();

    // 记录本次访问是否已拦截过
    const interceptOnce = ref(false);
    // 是否站内判断
    const isApp = computed(() => {
        return variable.data.IS_NN || variable.data.IS_MOOMOO;
    });

    /**
     * 获取拦截记录保存的 key
     * @returns {string} 固定前缀 + 设备ID + appId
     */
    const getKey = () => `FEGO:stay_modal_open_logs:${ deviceId }:${ appId }`;


    /**
     * 获取 dayjs 实例 （以服务端时间为准）
     */
    const getDayjsIns = (() => {
        // 获取服务器时间与客户端时间的时间差，确保后面的时间获取都是以服务端为准
        const serverClientTimeDiff = dayjs().diff(dayjs.unix(variable.data.NOW_SERVER || dayjs().unix()));
        return () => {
            // 获取客户端时间 dayjs 实例
            const now = dayjs();
            // 客户端时间 - （服务端时间与客户端时间差）= 当前正确的服务端时间
            return now.subtract(serverClientTimeDiff);
        };
    })();


    /**
     * 获取拦截记录 (localStorage)
     * @returns {array} []
     */
    const getInterceptRecords = () => {
        const key = getKey();
        const data = localStorage.getItem(key);
        return JSON.parse(data || '[]');
    };

    /**
     * 记录拦截记录 (localStorage)
     */
    const recordIntercept = () => {
        const time = getDayjsIns().unix();
        const key = getKey();
        const data = getInterceptRecords();
        data.push(time);
        localStorage.setItem(key, JSON.stringify(data));
    };


    /**
     * 检查本次是否应该拦截
     * @returns {boolean} true / false
     */
    const shouldIntercept = () => {
        try {
            // 站外不拦截
            if (!isApp.value) {
                return false;
            }

            // 本次访问已经拦截过了，不能重复拦截了 (否则一直弹)
            if (interceptOnce.value) {
                return false;
            }

            // 周期
            const interval = stayModalConfig.options?.frequencyInterval;
            // 次数
            let times = stayModalConfig.options?.frequencyTimes ?? 0;
            // 总的活动期间的上限
            const limit = stayModalConfig.options?.frequencyLimit ?? 0;
            // 获取拦截记录
            const records = getInterceptRecords();
            // 获取当前 dayjs 实例
            const dayjsNow = getDayjsIns();

            // 总的活动期间弹窗次数已经 > 弹窗上限
            if (records.length >= limit) {
                return false;
            }

            let start;
            if (interval === FREQUENCY_INTERVAL.DAILY) {
                // 每天 - 获取当天开始时间
                start = dayjsNow.startOf('day');
            } else if (interval === FREQUENCY_INTERVAL.WEEKILY) {
                // 每周 - 获取本周开始时间
                start = dayjsNow.startOf('week');
            } else if (interval === FREQUENCY_INTERVAL.THROUGHOUT_CAMPAIGN) {
                // 页面周期内 - 开始时间为 0
                start = 0;
                // 页面周期内 - 以 "上限" 为准
                times = limit;
            } else {
                // 不合法配置
                return false;
            }

            // 获取当前周期内的拦截次数
            const count = records.filter((time) => dayjs.unix(time).isAfter(start))?.length ?? 0;

            // 当前弹窗次数小于周期内上限，则可弹窗
            return count < times;
        } catch (err) {
            reportLog({
                message: '[站内挽留弹窗][shouldIntercept]拦截判断失败',
                err: errStr(err),
                tpl: props.tpl,
                appId
            }, 'error');
            console.error('[站内挽留弹窗][shouldIntercept]拦截判断失败', props.tpl.id, err);
            return false;
        }
    };

    /**
     * 初始化拦截
     * @param {function} fn 拦截后应该执行的回调函数
     */
    const initIntercept = (fn) => {
        // 插件配置未开启，不拦截
        if (!stayModalConfig?.enable) {
            return;
        }

        setTimeout(() => {
            if (window.NNWebViewJSBridge) {
                // 设置客户端协议回调函数及挂载到 window 上
                const openModalHandler = () => {
                    if (!shouldIntercept()) {
                        // 关闭
                        return nn.closeWebview();
                    }
                    // 记录本次已拦截
                    interceptOnce.value = true;
                    // 记录拦截记录
                    recordIntercept();
                    // 触发回调
                    fn?.();
                };
                const interceptCallbackName = `__stayModalCallbackOf${appId}`;
                window[interceptCallbackName] = openModalHandler;

                window.NNWebViewJSBridge.callHandler('interceptCloseBrowserAction', {
                    interceptedJSCode: `${interceptCallbackName}()`,
                }, (resp) => {
                    if (resp?.code !== 0) {
                        reportLog({
                            message: '[站内挽留弹窗]初始化拦截事件失败',
                            resp,
                            tpl: props.tpl,
                            appId
                        }, 'error');
                        console.error('[站内挽留弹窗]初始化拦截事件失败', props.tpl.id, resp);
                    } else {
                        console.error('[站内挽留弹窗]初始化拦截事件成功', props.tpl.id, resp);
                    }
                });
            }
        }, 100);
    };

    return {
        initIntercept
    };
}


